import React, { useState, useCallback, useEffect, useContext } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Tab from 'react-bootstrap/Tab'
import Button from 'react-bootstrap/Button'

import { StepContext } from '../StepContext'
import { TasaFA, TabsBimestre } from './UtilsFieldArray'
import { TotalBimestreFA, TotalAnualFA } from './Totales'
import { getFirstError, goToFirstError } from '../components/Errores'
import DatosActividad from './DatosActividad'
import CargandoDatosLoader from '../components/CargandoDatosLoader'

import {
  requestDeterminacionTasaPrecarga,
  requestDeterminacionTasa,
  storeDeterminacionTasa,
  emptyPromise
} from '../utils/requests'

const itemNuevo = {
  cuit: '',
  fecha: '',
  comprobante: '',
  importe: ''
}

const defaultValues = [1, 2, 3, 4, 5, 6].reduce((acc, n) => {
  let item = {}
  item[`bimestre${n}`] = {
    name: `bimestre${n}`,
    bimestre: n,
    legajo: {
      retenciones: [],
      percepciones: []
    }
  }
  return { ...acc, ...item }
}, {})

function DeterminacionTasa() {
  const { setPuedeAvanzar, setCallback } = useContext(StepContext)
  const [loading, setLoading] = useState(true)
  const [datos, setDatos] = useState([])
  const [errorBimestre, setErrorBimestre] = useState(null)
  const [bimestreActivo, setBimestreActivo] = useState(1)

  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues
  })

  const {
    getValues,
    reset,
    trigger,
    formState: { errors, isValid }
  } = methods

  const storeData = useCallback(async () => {
    const result = await trigger()
    if (!isValid && !result) {
      if (!result) goToFirstError(errors, setBimestreActivo)
      return emptyPromise()
    }
    // Hack! Por como estan estructurados los datos del form, de esta manera
    // obtengo un array plano de objetos con los datos necesarios para
    // meterlos directo en el backend a la db
    const datos = JSON.stringify(
      Object.values(getValues({ nest: true }))
        .flatMap((x) => Object.values(x))
        .flatMap((x) => Object.values(x))
    )

    return storeDeterminacionTasa(datos).then(
      ({ status, content }) => status === 200
    )
  }, [getValues, trigger, errors, isValid])

  useEffect(() => {
    setLoading(true)

    const urlParams = new URLSearchParams(window.location.search)
    setErrorBimestre(urlParams.get('error-bimestre'))

    requestDeterminacionTasa()
      .then(({ status, content }) => setDatos(content.data))
      .finally(() => setLoading(false))
  }, [setLoading, setDatos])

  useEffect(() => {
    setLoading(true)

    requestDeterminacionTasaPrecarga()
      .then(({ status, content }) => {
        const bimestres = content?.imponibles?.reduce((acc, imponible) => {
          const name = `bimestre${imponible.bimestre}`
          const legajo = `leg${imponible.legajo}`

          let act = `act${imponible.catID}`

          if (!imponible.catID && datos.length === 1) {
            imponible.catID = datos[0].actividad
            act = `act${datos[0].actividad}`
          }

          let bimestres = { ...acc }

          if (bimestres?.[name]?.[legajo]) {
            bimestres[name][legajo][act] = imponible
            return bimestres
          }

          if (bimestres?.[name]) {
            bimestres[name][legajo] = {}
            bimestres[name][legajo][act] = imponible
            return bimestres
          }

          bimestres[name] = {}
          bimestres[name][legajo] = {}
          bimestres[name][legajo][act] = imponible

          return bimestres
        }, {})

        content?.retenciones?.forEach((r) => {
          const bimestre = `bimestre${r.bimestre}`
          const legajo = `leg${r.legajo}`
          const values = Object.values(bimestres[bimestre][legajo])
          const act = values?.slice(-1)[0].catID
          if (bimestres[bimestre][legajo][`act${act}`].retenciones) {
            bimestres[bimestre][legajo][`act${act}`].retenciones.push(r)
          } else {
            bimestres[bimestre][legajo][`act${act}`].retenciones = [r]
          }
        })

        content?.percepciones?.forEach((p) => {
          const bimestre = `bimestre${p.bimestre}`
          const legajo = `leg${p.legajo}`
          const values = Object.values(bimestres[bimestre][legajo])
          const act = values?.slice(-1)[0].catID
          if (bimestres[bimestre][legajo][`act${act}`].percepciones) {
            bimestres[bimestre][legajo][`act${act}`].percepciones.push(p)
          } else {
            bimestres[bimestre][legajo][`act${act}`].percepciones = [p]
          }
        })

        reset(bimestres)
      })
      .finally(() => setLoading(false))
  }, [datos, reset])

  useEffect(() => {
    setPuedeAvanzar(true)
    setCallback(storeData)
  }, [storeData, setCallback, setPuedeAvanzar])

  return (
    <>
      <h6 className="text-muted text-normal text-uppercase">
        Determinación de la tasa
      </h6>
      <hr className="m-0 mb-4" />

      {getFirstError(errors) !== null ? (
        <Button
          style={{ position: 'fixed', bottom: '0', right: '0', zIndex: 99 }}
          onClick={() => goToFirstError(errors, setBimestreActivo)}
        >
          Ir al siguiente error
        </Button>
      ) : (
        <></>
      )}

      <CargandoDatosLoader cargando={loading}>
        <FormProvider {...methods}>
          <Form>
            {errorBimestre && (
              <Alert variant={'danger'} style={{ marginBottom: '1em' }}>
                Los montos ingresados para el/los bimestre/s {errorBimestre} son
                incorrectos. Comuníquese con la Dirección de Ingresos Públicos
              </Alert>
            )}
            <Tab.Container
              className="tabs-bimestres"
              activeKey={bimestreActivo}
              onSelect={(n) => setBimestreActivo(n)}
            >
              <TabsBimestre values={Object.values(defaultValues)} />

              <Tab.Content className="w-100">
                {Object.values(defaultValues).map((state, index) => (
                  <Tab.Pane key={index} eventKey={state.bimestre}>
                    {datos?.map((data, i) => {
                      const minimoPrincipal = datos.filter(
                        (item) =>
                          item.legajo === data.legajo && item.tipo === 'P'
                      )[0].minimoTasa

                      return (
                        <React.Fragment key={i}>
                          <DatosActividad
                            datos={datos}
                            data={data}
                            bimestre={state.name}
                            legajo={`leg${data.legajo}`}
                            actividad={`act${data.actividad}`}
                            minimoPrincipal={minimoPrincipal}
                          />

                          {
                            // Muestro botones para agregar retenciones y
                            // percepciones solo despues de la ultima actividad de
                            // un mismo legajo
                          }
                          {datos
                            .map((d) => d.legajo)
                            .lastIndexOf(data.legajo) === i ? (
                            <>
                              <TasaFA
                                name={`bimestre${state.bimestre}.leg${data.legajo}.act${data.actividad}.retenciones`}
                                bimestre={state.name}
                                legajo={`leg${data.legajo}`}
                                actividad={`act${data.actividad}`}
                                arrayName="retenciones"
                                property="retencion"
                                text="Retención"
                                item={itemNuevo}
                              />

                              <TasaFA
                                name={`bimestre${state.bimestre}.leg${data.legajo}.act${data.actividad}.percepciones`}
                                bimestre={state.name}
                                legajo={`leg${data.legajo}`}
                                actividad={`act${data.actividad}`}
                                arrayName="percepciones"
                                property="percepcion"
                                text="Percepción"
                                item={itemNuevo}
                              />
                            </>
                          ) : (
                            <></>
                          )}
                        </React.Fragment>
                      )
                    })}
                    <TotalBimestreFA
                      bimestre={state.name}
                      titulo={`Total bimestre ${state.bimestre}`}
                    />
                  </Tab.Pane>
                ))}
              </Tab.Content>

              <TotalAnualFA />
            </Tab.Container>
          </Form>
        </FormProvider>
      </CargandoDatosLoader>
    </>
  )
}

export default DeterminacionTasa
